import { Box, ColorMode, Text, VStack } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { format, isSameDay, isToday } from 'date-fns';
import { SingleDayEventDots } from '.';
import { MultiDayEventLines } from '.';
import { getEventDays } from '.';
import { ICalendarPlusSettings, IEvent } from '../../../../lib';

interface IDateCellsProps {
	currRange: {
		date: Date;
		isPlaceholder: boolean;
	}[][];
	events: IEvent[];
	selectedDate: Date;
	settings: ICalendarPlusSettings;
	colorMode: ColorMode;
	setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
}

export const DateCells: React.FunctionComponent<IDateCellsProps> = ({
	colorMode,
	currRange,
	events,
	selectedDate,
	settings,
	setSelectedDate,
}) => {
	const createOnClickHandler = useCallback(
		(date: Date, isPlaceholder: boolean) => {
			return () => {
				if (!isPlaceholder) {
					setSelectedDate(date);
				}
			};
		},
		[setSelectedDate]
	);
	return (
		<>
			{currRange.map((week, weekIndex) => {
				let trackEvents: IEvent[] = [];
				return (
					<React.Fragment key={weekIndex}>
						{week.map((day, dayIndex) => {
							const isTodayDay = isToday(day.date);
							const singleDayEvents = events.filter(
								(event) => isSameDay(event.start, day.date) && isSameDay(event.end, day.date)
							);
							const multiDayEvents = events
								.filter((event) => {
									const eventDays = getEventDays(event);
									return eventDays.length > 1;
								})
								.sort((event1, event2) => {
									return event1.start.getTime() - event2.start.getTime();
								});

							// Filter out only the events not already in trackEvents
							const newEvents = multiDayEvents.filter(
								(event) => !trackEvents.some((tevent) => tevent.id === event.id)
							);
							// Combine new events with existing trackEvents
							trackEvents = [...trackEvents, ...newEvents];

							const isPlaceholder = day.isPlaceholder;
							const isSelectedDay = selectedDate && isSameDay(day.date, selectedDate);

							const onClickHandler = createOnClickHandler(day.date, isPlaceholder);
							return (
								<Box
									key={dayIndex}
									textAlign='center'
									position='relative'
									borderRight={week.length - 1 !== dayIndex ? '1px solid' : ''}
									borderBottom={currRange.length - 1 !== weekIndex ? '1px solid' : ''}
									borderColor={settings.styles[colorMode].calendarBordersColor}
									className={`custom-month-day-cell ${isPlaceholder ? 'rbc-off-range-bg' : ''}`}
									height='full'
									fontWeight={isTodayDay ? 'bold' : 'normal'}
									backgroundColor={
										isSelectedDay
											? settings.styles[colorMode].calendarSelectedCellBackgroundColor
											: isTodayDay
											? settings.styles[colorMode].calendarTodayCellBackgroundColor
											: settings.styles[colorMode].calendarBackgroundColor
									}
									display='flex'
									flexDirection='column'
									justifyContent='center'
									alignItems='center'
									paddingBottom={0}
									onClick={onClickHandler}
								>
									<Text
										mb={1}
										color={isPlaceholder ? 'gray.500' : 'inherit'}
										fontSize={{ base: 'xs', sm: 'sm' }}
									>
										{isPlaceholder ? '-' : format(day.date, 'dd')}
									</Text>

									{/* Display multi-day events */}
									{!isPlaceholder && multiDayEvents.length > 0 && (
										<VStack spacing={0} align='start' width='full' height='full'>
											<MultiDayEventLines
												events={multiDayEvents}
												dayDate={day.date}
												trackEvents={trackEvents}
												eventColor={settings.styles[colorMode]?.eventColor}
												settings={settings}
											/>
										</VStack>
									)}

									{/* Display single-day events as dots */}
									{!isPlaceholder && singleDayEvents.length > 0 && (
										<SingleDayEventDots
											events={singleDayEvents}
											color={settings.styles[colorMode]?.eventColor}
											settings={settings}
										/>
									)}
								</Box>
							);
						})}
					</React.Fragment>
				);
			})}
		</>
	);
};
